import type { B2bCatalog, B2bShipTo, OrderLineInput, OrderQuote, QuotedLine } from '@api/b2b';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import type { FilterValue } from '@components/ui/EntityFilterDropdown';
import { Button, Checkbox, Modal, NumberInput, Select, TextInput, Textarea } from '@mantine/core';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { useDebouncedValue } from '@mantine/hooks';
import React, { useEffect, useMemo, useState } from 'react';
import styles from './b2b.module.css';
import { freeColumn } from './freeColumn';
import { Card, Field, ProductCell, QuoteTotals, SchemeStrip } from './shared';
import { useFreePickDefaults } from './useFreePickDefaults';
import { errorMessage, freeChosen, freeEligibility, freePicksComplete, matchesProductFilter, money, productFilterParams, sumLines, units, variantLabel } from './utils';


export interface PlaceOrderInput {
    lines: OrderLineInput[];
    freePicks?: OrderLineInput[];
    shipToWarehouseId?: string;
    customerReference?: string;
    notes?: string;
}

interface OrderFormProps {
    catalog: B2bCatalog;
    shipTos: B2bShipTo[];
    quote: (lines: OrderLineInput[], freePicks: OrderLineInput[]) => Promise<OrderQuote>;
    onPlace: (input: PlaceOrderInput) => Promise<void>;
    placeLabel?: string;
    /** The brand's team placing the order for the customer: wording speaks about the customer, not to them. */
    onBehalf?: boolean;
    /** The customer's name, for the review step when the brand places the order. */
    customerName?: string;
}

interface ReviewRow {
    key: string;
    name: string;
    quantity: number;
    unitPrice: number;
    lineTotal: number;
    free: boolean;
}

/**
 * The checkout step: every line with quantity, price and value, free pieces, the scheme discounts
 * and the payable amount (the same server quote the PI is built from), then where it ships, the PO
 * number and notes. Back to edit returns to the catalogue.
 */
const ReviewOrderModal: React.FC<{
    quote: OrderQuote;
    shipTos: B2bShipTo[];
    shipTo: string | null;
    onShipTo: (value: string | null) => void;
    reference: string;
    onReference: (value: string) => void;
    notes: string;
    onNotes: (value: string) => void;
    placeLabel: string;
    onBehalf: boolean;
    customerName?: string;
    placing: boolean;
    error: string | null;
    onBack: () => void;
    onConfirm: () => void;
}> = ({ quote, shipTos, shipTo, onShipTo, reference, onReference, notes, onNotes, placeLabel, onBehalf, customerName, placing, error, onBack, onConfirm }) => {
    const rows: ReviewRow[] = [
        ...quote.lines.map((l) => ({ key: l.productId, name: `${l.name} (${l.sku})`, quantity: l.quantity, unitPrice: l.unitPrice, lineTotal: l.lineTotal, free: false })),
        ...quote.freeLines.map((l, i) => ({ key: `free-${i}`, name: [l.category, variantLabel(l)].filter(Boolean).join(' · '), quantity: l.quantity, unitPrice: l.unitPrice, lineTotal: l.lineTotal, free: true })),
    ];
    const totalUnits = rows.reduce((sum, r) => sum + r.quantity, 0);
    const columns: DataSheetColumn<ReviewRow>[] = [
        { key: 'name', header: 'Product', width: 300, render: (v: string, r) => (r.free ? `${v} · free` : v) },
        { key: 'quantity', header: 'Quantity', width: 100, align: 'right', render: (v: number) => units(v) },
        { key: 'unitPrice', header: 'Price', width: 110, align: 'right', render: (v: number) => money(v) },
        { key: 'lineTotal', header: 'Value', width: 120, align: 'right', render: (v: number) => money(v) },
    ];
    return (
        <Modal opened onClose={onBack} title={onBehalf ? `Review the order for ${customerName ?? 'the customer'}` : 'Review your order'} centered size="xl">
            <div className={styles.stack}>
                <span className={styles.cardHint}>
                    {quote.lines.length} product{quote.lines.length === 1 ? '' : 's'}, {units(totalUnits)} units{quote.freeLines.length ? ' including free pieces' : ''}. Check quantities and
                    prices before placing.
                </span>
                <DataSheet<ReviewRow> bare hideHeaderBar data={rows} columns={columns} getRowKey={(r) => r.key} maxHeight="auto" />
                <QuoteTotals quote={quote} />
                <div className={styles.fieldGridTop}>
                    <Select
                        label="Ship to"
                        size="sm"
                        data={shipTos.map((s) => ({ value: s.warehouseId, label: `${s.name} · ${s.address}` }))}
                        value={shipTo}
                        onChange={onShipTo}
                    />
                    <TextInput
                        label={onBehalf ? "Customer's PO number" : 'Your PO number'}
                        description="Optional. Printed on the PI."
                        inputWrapperOrder={['label', 'input', 'description', 'error']}
                        size="sm"
                        value={reference}
                        onChange={(e) => onReference(e.currentTarget.value)}
                    />
                </div>
                <Textarea label="Notes" size="sm" autosize minRows={2} value={notes} onChange={(e) => onNotes(e.currentTarget.value)} />
                <span className={styles.muted}>
                    {onBehalf
                        ? 'It goes to review like any portal order: check it on Review & PI, then send the PI.'
                        : 'We will check your order and send you a proforma invoice. You pay only after confirming the PI.'}
                </span>
                {error ? <span className={styles.error}>{error}</span> : null}
                <div className={styles.rowEnd}>
                    <Button variant={ButtonVariant.DEFAULT} disabled={placing} onClick={onBack}>
                        Back to edit
                    </Button>
                    <Button variant={ButtonVariant.ACTION} loading={placing} disabled={!shipTo} onClick={onConfirm}>
                        {placeLabel}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

/**
 * The basket: the schemes line on top, the customer's priced catalogue with Quantity and Free side
 * by side, and an order bar at the bottom with the totals as the server computes them (quoted on
 * every change, so the screen and the PI can never disagree). Ship-to, PO number and notes are
 * taken in the review pop-up.
 */
const OrderForm: React.FC<OrderFormProps> = ({ catalog, shipTos, quote, onPlace, placeLabel = 'Place order', onBehalf = false, customerName }) => {
    const [quantities, setQuantities] = useState<Record<string, number>>({});
    const [freePicks, setFreePicks] = useState<OrderLineInput[]>([]);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState<FilterValue>({});
    const [onlyOrdered, setOnlyOrdered] = useState(false);
    const [shipTo, setShipTo] = useState<string | null>(shipTos[0]?.warehouseId ?? null);
    const [reference, setReference] = useState('');
    const [notes, setNotes] = useState('');
    const [current, setCurrent] = useState<OrderQuote | null>(null);
    const [quoteError, setQuoteError] = useState<string | null>(null);
    const [placing, setPlacing] = useState(false);
    const [reviewing, setReviewing] = useState(false);
    const [placeError, setPlaceError] = useState<string | null>(null);

    const lines = useMemo(
        () => Object.entries(quantities).filter(([, q]) => q > 0).map(([productId, quantity]) => ({ productId, quantity })),
        [quantities],
    );
    const picks = useMemo(() => freePicks.filter((p) => p.productId && p.quantity > 0), [freePicks]);
    const [debounced] = useDebouncedValue(JSON.stringify({ lines, picks }), 400);

    useEffect(() => {
        const { lines: l, picks: p } = JSON.parse(debounced) as { lines: OrderLineInput[]; picks: OrderLineInput[] };
        if (l.length === 0) {
            setCurrent(null);
            return;
        }
        let stale = false;
        quote(l, p)
            .then((q) => {
                if (!stale) {
                    setCurrent(q);
                    setQuoteError(null);
                }
            })
            .catch((error) => !stale && setQuoteError(errorMessage(error)));
        return () => {
            stale = true;
        };
    }, [debounced, quote]);

    const rows = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return catalog.products.filter(
            (p) =>
                (!needle || p.name.toLowerCase().includes(needle) || p.sku.toLowerCase().includes(needle)) &&
                matchesProductFilter(p, filter) &&
                (!onlyOrdered || (quantities[p.productId] ?? 0) > 0 || picks.some((f) => f.productId === p.productId)),
        );
    }, [catalog.products, search, filter, onlyOrdered, quantities, picks]);
    const filterParams = useMemo(() => productFilterParams(catalog.products), [catalog.products]);
    const detailLabels = useMemo(() => [...new Set(catalog.products.flatMap((p) => p.details.map((d) => d.label)))], [catalog.products]);

    const entitlements = useMemo(() => current?.schemes.entitlements ?? [], [current]);
    useFreePickDefaults(current, freePicks, setFreePicks);
    const eligible = useMemo(() => freeEligibility(entitlements, current?.lines ?? []), [entitlements, current]);

    const columns = useMemo<DataSheetColumn<QuotedLine>[]>(
        () => [
            { key: 'name', header: 'Product', width: 280, sticky: true, render: (_v, row) => <ProductCell name={row.name} sku={row.sku} /> },
            { key: 'category', header: 'Category', width: 140, render: (v: string | null) => v ?? '—' },
            // One column per product detail the brand has set (e.g. Size, Colour), in its order.
            ...detailLabels.map<DataSheetColumn<QuotedLine>>((label) => ({
                key: `detail:${label}`,
                header: label,
                width: 110,
                valueAccessor: (row) => row.details.find((d) => d.label === label)?.value ?? null,
                render: (_v, row) => row.details.find((d) => d.label === label)?.value ?? '—',
            })),
            { key: 'mrp', header: 'MRP', width: 100, align: 'right', render: (v: number | null) => money(v) },
            { key: 'unitPrice', header: onBehalf ? 'Customer price' : 'Your price', width: 110, align: 'right', render: (v: number) => money(v) },
            { key: 'gstRate', header: 'GST', width: 70, align: 'right', render: (v: number | null) => (v === null ? '—' : `${v}%`) },
            {
                key: 'quantity',
                header: 'Quantity',
                width: 130,
                render: (_v, row) => (
                    <NumberInput
                        size="xs"
                        min={0}
                        allowDecimal={false}
                        className={styles.qtyInput}
                        value={quantities[row.productId] ?? ''}
                        onChange={(value) => setQuantities((q) => ({ ...q, [row.productId]: Number(value) || 0 }))}
                        aria-label={`Quantity of ${row.name}`}
                    />
                ),
            },
            freeColumn<QuotedLine>(eligible, picks, setFreePicks),
            {
                key: 'lineTotal',
                header: 'Value',
                width: 120,
                align: 'right',
                render: (_v, row) => ((quantities[row.productId] ?? 0) > 0 ? money((quantities[row.productId] ?? 0) * row.unitPrice) : ''),
            },
        ],
        [quantities, onBehalf, eligible, picks, detailLabels],
    );

    const freeIncomplete = !freePicksComplete(entitlements, picks);
    const byId = useMemo(() => new Map(catalog.products.map((p) => [p.productId, p])), [catalog.products]);
    const categorised = useMemo(() => lines.map((l) => ({ ...l, category: byId.get(l.productId)?.category ?? null })), [lines, byId]);

    const errors = [...(current?.errors ?? []), ...(quoteError ? [quoteError] : [])];
    const chosen = freeChosen(entitlements, picks);
    const short = entitlements.filter((e) => e.units > 0 && (chosen.get(e.schemeCode) ?? 0) !== e.units);
    const canReview = !!current && current.errors.length === 0 && !freeIncomplete && lines.length > 0 && !placing;
    const paid = sumLines(current?.lines ?? []);
    const free = sumLines(current?.freeLines ?? []);

    const place = async () => {
        setPlacing(true);
        setPlaceError(null);
        try {
            await onPlace({
                lines,
                freePicks: picks.length ? picks : undefined,
                shipToWarehouseId: shipTo ?? undefined,
                customerReference: reference.trim() || undefined,
                notes: notes.trim() || undefined,
            });
        } catch (error) {
            setPlaceError(errorMessage(error));
        } finally {
            setPlacing(false);
        }
    };

    return (
        <div className={styles.stack}>
            {catalog.instructions && !onBehalf ? <Card hint={catalog.instructions} /> : null}
            <SchemeStrip
                schemes={catalog.schemes}
                lines={categorised}
                entitlements={entitlements}
                picks={picks}
                emptyText={`No scheme is running for ${onBehalf ? 'this customer' : 'you'} today.`}
            />
            <DataSheet<QuotedLine>
                title="Catalogue"
                subtitle={`${catalog.products.length} products at ${onBehalf ? "the customer's" : 'your'} prices${eligible.size ? ' · free pieces pre-filled in the Free column' : ''}`}
                data={rows}
                columns={columns}
                getRowKey={(row) => row.productId}
                enableSearch
                searchPlaceholder="Search name or SKU"
                searchValue={search}
                onSearchChange={setSearch}
                filters={[{ label: 'Product', params: filterParams, value: filter, onChange: setFilter }]}
                toolbarControls={<Checkbox size="xs" label={onBehalf ? 'Only what is ordered' : "Only what I've ordered"} checked={onlyOrdered} onChange={(e) => setOnlyOrdered(e.currentTarget.checked)} />}
                maxHeight="auto"
                emptyState={onBehalf ? 'No products are priced for this customer yet. Set prices under Price lists & schemes.' : 'No products are priced for you yet. Contact the brand.'}
            />
            <div className={styles.orderBar}>
                <div className={styles.orderFigures}>
                    <Field label="Products" value={`${units(paid.units)} pcs · ${money(paid.value)}`} />
                    <Field label="Free" value={`${units(free.units)} pcs`} />
                    <Field label="Payable (incl. GST)" value={current ? money(current.totals.payable) : '—'} />
                </div>
                <div className={styles.row}>
                    {catalog.minOrderValue > 0 ? <span className={styles.muted}>Minimum order {money(catalog.minOrderValue)} before tax.</span> : null}
                    {[...short.map((e) => `${e.schemeName}: ${units(chosen.get(e.schemeCode) ?? 0)} of ${units(e.units)} free chosen`), ...errors].map((e) => (
                        <span key={e} className={styles.error}>
                            {e}
                        </span>
                    ))}
                    <Button
                        variant={ButtonVariant.ACTION}
                        disabled={!canReview}
                        onClick={() => {
                            setPlaceError(null);
                            setReviewing(true);
                        }}
                    >
                        Review &amp; place
                    </Button>
                </div>
            </div>
            {reviewing && current ? (
                <ReviewOrderModal
                    quote={current}
                    shipTos={shipTos}
                    shipTo={shipTo}
                    onShipTo={setShipTo}
                    reference={reference}
                    onReference={setReference}
                    notes={notes}
                    onNotes={setNotes}
                    placeLabel={placeLabel}
                    onBehalf={onBehalf}
                    customerName={customerName}
                    placing={placing}
                    error={placeError}
                    onBack={() => setReviewing(false)}
                    onConfirm={() => void place()}
                />
            ) : null}
        </div>
    );
};

export default OrderForm;
